import StarIcon from "@mui/icons-material/Star";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Rating from "@mui/material/Rating";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { Gauge } from "@mui/x-charts/Gauge";
import PropTypes from "prop-types";

export default function CSATWidget(props) {
  const theme = useTheme();
  const data = props.data || {};

  // CSAT data structure
  const averageScore = data.averageScore || 0;
  const totalResponses = data.totalResponses || 0;
  const distribution = data.distribution || [];
  const changePercent = data.changePercent || 0;

  // Color coding for CSAT scores
  const getScoreColor = (score) => {
    if (score >= 4.5) return theme.palette.success.main;
    if (score >= 3.5) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  // Generate smiley/emoji based on score
  const getSmileyEmoji = (score) => {
    if (score >= 4.5) return "😊";
    if (score >= 4.0) return "🙂";
    if (score >= 3.0) return "😐";
    if (score >= 2.0) return "🙁";
    return "😞";
  };

  return (
    <>
      <Stack sx={{ justifyContent: "space-between", mb: 2 }}>
        <Stack
          direction="row"
          sx={{
            alignContent: { xs: "center", sm: "flex-start" },
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography
            variant="h4"
            component="p"
            sx={{ color: getScoreColor(averageScore) }}
          >
            {averageScore.toFixed(1)}
          </Typography>
          <Typography variant="h5" component="span" sx={{ fontSize: "2rem" }}>
            {getSmileyEmoji(averageScore)}
          </Typography>
          <Chip
            size="small"
            color={changePercent > 0 ? "success" : "error"}
            label={`${changePercent > 0 ? "+" : ""}${changePercent}%`}
          />
        </Stack>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {props.title || "Customer Satisfaction Score"}
        </Typography>
      </Stack>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* CSAT Gauge */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 120,
          }}
        >
          <Gauge
            value={averageScore}
            valueMin={0}
            valueMax={5}
            startAngle={-110}
            endAngle={110}
            sx={{
              [`& .MuiGauge-valueText`]: {
                fontSize: 20,
                transform: "translate(0px, 0px)",
                fill: getScoreColor(averageScore),
                fontWeight: 600,
              },
              [`& .MuiGauge-valueArc`]: {
                fill: getScoreColor(averageScore),
              },
            }}
            text={({ value }) => `${value.toFixed(1)}/5.0`}
          />
        </Box>

        {/* Star Rating Display */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Rating
            value={averageScore}
            precision={0.1}
            readOnly
            size="large"
            icon={<StarIcon fontSize="inherit" />}
            emptyIcon={<StarIcon fontSize="inherit" />}
          />
          <Typography variant="body2" sx={{ color: "text.secondary", ml: 1 }}>
            ({totalResponses} responses)
          </Typography>
        </Box>

        {/* Distribution Bar Chart */}
        {/* {distribution.length > 0 && (
          <Box sx={{ height: 100, mt: 1 }}>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", mb: 1, display: "block" }}
            >
              Rating Distribution
            </Typography>
            <BarChart
              xAxis={[
                {
                  scaleType: "band",
                  data: distribution.map((item) => `${item.stars}★`),
                  categoryGapRatio: 0.3,
                },
              ]}
              series={[
                {
                  data: distribution.map((item) => item.count),
                  color: theme.palette.primary.main,
                },
              ]}
              height={80}
              margin={{ left: 30, right: 20, top: 10, bottom: 30 }}
              slotProps={{
                legend: { hidden: true },
              }}
            />
          </Box>
        )} */}

        {/* Summary Stats */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            pt: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h6"
              component="p"
              sx={{ color: theme.palette.success.main }}
            >
              {distribution
                .filter((item) => item.stars >= 4)
                .reduce((sum, item) => sum + item.count, 0)}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Positive (4-5★)
            </Typography>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h6"
              component="p"
              sx={{ color: theme.palette.warning.main }}
            >
              {distribution
                .filter((item) => item.stars === 3)
                .reduce((sum, item) => sum + item.count, 0)}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Neutral (3★)
            </Typography>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h6"
              component="p"
              sx={{ color: theme.palette.error.main }}
            >
              {distribution
                .filter((item) => item.stars <= 2)
                .reduce((sum, item) => sum + item.count, 0)}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Negative (1-2★)
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
}

CSATWidget.propTypes = {
  data: PropTypes.object,
  title: PropTypes.string,
};
